import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Segment, Button, Icon, Input, Checkbox, Form, Dropdown } from 'semantic-ui-react';

import styles from './FieldEditor.module.scss';

const FieldEditor = React.memo(
  ({ field, index, isDragging, dragHandleProps, onUpdate, onDelete }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const handleUpdateField = updates => {
      onUpdate({ ...field, ...updates });
    };

    // Estado local para evitar que el cursor salte al inicio en textarea controlado
    const [optionsText, setOptionsText] = useState(field.options?.join('\n') || '');

    useEffect(() => {
      setOptionsText(field.options?.join('\n') || '');
    }, [field.options, field.type]);

    const handleCommitOptions = text => {
      const list = (text || '')
        .split(/[,\n]/)
        .map(opt => opt.trim())
        .filter(opt => opt.length > 0);
      handleUpdateField({ options: list });
    };

    const needsOptions = ['select', 'radio', 'checkbox'].includes(field.type);

    return (
      <Segment className={`${styles.fieldCard} ${isDragging ? styles.dragging : ''}`}>
        <div className={styles.fieldHeader} onClick={() => setIsExpanded(!isExpanded)}>
          <div className={styles.fieldInfo}>
            <div {...dragHandleProps} className={styles.dragHandle} title="Drag to reorder">
              <Icon name="ellipsis vertical" />
            </div>
            <Icon name={getFieldIcon(field.type)} />
            <span className={styles.fieldLabel}>{field.label || 'New Field'}</span>
            <span className={styles.fieldType}>({field.type})</span>
            {field.required && <Icon name="asterisk" size="small" color="red" />}
          </div>

          <div className={styles.fieldActions}>
            <Button
              size="mini"
              icon="edit"
              basic
              title="Edit field"
              onClick={e => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
            />
            <Button
              size="mini"
              icon="trash"
              basic
              color="red"
              title="Delete field"
              onClick={e => {
                e.stopPropagation();
                onDelete();
              }}
            />
          </div>
        </div>

        {isExpanded && (
          <div className={styles.fieldConfig}>
            <Form size="small">
              <Form.Field>
                <label>Field Label</label>
                <Input
                  value={field.label}
                  onChange={e => handleUpdateField({ label: e.target.value })}
                  placeholder="New Field"
                />
              </Form.Field>

              <Form.Field>
                <label>Field Name (internal)</label>
                <Input
                  value={field.name}
                  onChange={e => handleUpdateField({ name: e.target.value })}
                  placeholder="e.g., vehicleCondition"
                />
              </Form.Field>

              {needsOptions && (
                <Form.Field>
                  <label>Options (one per line or comma-separated)</label>
                  <textarea
                    rows={3}
                    value={optionsText}
                    onChange={e => setOptionsText(e.target.value)}
                    onBlur={() => handleCommitOptions(optionsText)}
                    placeholder="Option 1&#10;Option 2&#10;Option 3"
                  />
                  <small className={styles.hint}>
                    Press Enter to add a new option or use commas.
                  </small>
                </Form.Field>
              )}

              {field.type === 'card_extraction' && (
                <Form.Field>
                  <label>Extraction Key (from Description)</label>
                  <Input
                    value={field.extractionKey || ''}
                    onChange={e => handleUpdateField({ extractionKey: e.target.value })}
                    placeholder="e.g. Name, Phone, [full description], [damages]"
                  />
                  <small className={styles.hint}>
                    Use <code>[full description]</code> to get the entire description,{' '}
                    <code>[damages]</code> to extract the vehicle damages table, or use a key like
                    "Phone" to find "Phone: 555-0123".
                  </small>
                </Form.Field>
              )}

              {field.type === 'variable' && (
                <>
                  <Form.Field>
                    <label>Variable</label>
                    <Dropdown
                      selection
                      options={[
                        { key: 'card.name', value: 'card.name', text: 'Card Title' },
                        { key: 'card.id', value: 'card.id', text: 'Card ID' },
                        {
                          key: 'card.dashboard.vehicleId',
                          value: 'card.dashboardData.data.vehicleId',
                          text: 'Vehicle Wheelbase ID (from card)',
                        },
                      ]}
                      value={field.variableKey || 'card.name'}
                      onChange={(_, { value }) =>
                        handleUpdateField({
                          variableKey: value,
                          variableLabel:
                            value === 'card.name'
                              ? 'Card Title'
                              : value === 'card.id'
                              ? 'Card ID'
                              : 'Vehicle Wheelbase ID (from card)',
                        })
                      }
                    />
                  </Form.Field>
                  <Form.Field>
                    <Checkbox
                      label="Read-only (user cannot change)"
                      checked={!!field.readOnly}
                      onChange={(e, { checked }) => handleUpdateField({ readOnly: checked })}
                    />
                  </Form.Field>
                </>
              )}

              {field.type === 'static_text' && (
                <Form.Field>
                  <label>Static Text</label>
                  <textarea
                    rows={4}
                    value={field.formattedText || ''}
                    onChange={e => handleUpdateField({ formattedText: e.target.value })}
                    placeholder="This text will be shown as read-only content within the form."
                  />
                </Form.Field>
              )}

              {field.type === 'link' && (
                <>
                  <Form.Field>
                    <label>Link URL</label>
                    <Input
                      value={field.url || ''}
                      onChange={e => handleUpdateField({ url: e.target.value })}
                      placeholder="https://example.com"
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>Link Text (optional, defaults to URL)</label>
                    <Input
                      value={field.label || ''}
                      onChange={e => handleUpdateField({ label: e.target.value })}
                      placeholder="Click here"
                    />
                  </Form.Field>
                </>
              )}

              {field.type === 'textarea' && (
                <Form.Field>
                  <label>Rows</label>
                  <Input
                    type="number"
                    value={field.rows || 3}
                    onChange={e => handleUpdateField({ rows: parseInt(e.target.value) })}
                    min={2}
                    max={10}
                  />
                </Form.Field>
              )}

              <Form.Field>
                <Checkbox
                  label="Required field"
                  checked={field.required}
                  onChange={(e, { checked }) => handleUpdateField({ required: checked })}
                />
              </Form.Field>

              {/* TODO: Conditional logic */}
            </Form>
          </div>
        )}
      </Segment>
    );
  }
);

// Helper para iconos por tipo de campo
function getFieldIcon(type) {
  const icons = {
    text: 'font',
    textarea: 'align left',
    number: 'hashtag',
    select: 'dropdown',
    radio: 'dot circle outline',
    checkbox: 'check square outline',
    date: 'calendar',
    time: 'clock outline',
    camera: 'camera',
    signature: 'pencil',
    static_text: 'info circle',
    link: 'linkify',
    wheelbase_vehicle: 'car',
    wheelbase_booking: 'clipboard list',
    variable: 'code',
    card_extraction: 'magic',
  };
  return icons[type] || 'question';
}

FieldEditor.propTypes = {
  field: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isDragging: PropTypes.bool,
  dragHandleProps: PropTypes.object,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default FieldEditor;
